// src/libs/location/geoCache.ts

export type LatLng = { lat: number; lng: number };

export type GeoCacheOptions = {
  /** storage key */
  key?: string;
  /** TTL 毫秒，預設 30 分鐘 */
  ttlMs?: number;
  /** 用 sessionStorage 或 localStorage（預設 sessionStorage） */
  storage?: 'session' | 'local';
};

const DEFAULT_KEY = 'geo_pos_v1';
const DEFAULT_TTL = 30 * 60 * 1000; // 30min

type Cached = LatLng & { ts: number };

// module memory cache（同一個 tab 的 runtime 內最優先）
const memoryCache = new Map<string, Cached>();

function getStorage(kind: 'session' | 'local'): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function isValidLatLng(lat: number, lng: number) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

export function readCachedPosition(opts: GeoCacheOptions = {}): LatLng | null {
  const key = opts.key ?? DEFAULT_KEY;
  const ttlMs = opts.ttlMs ?? DEFAULT_TTL;
  const storageKind = opts.storage ?? 'session';

  // 1) memory
  const mem = memoryCache.get(key);
  if (mem && Date.now() - mem.ts < ttlMs && isValidLatLng(mem.lat, mem.lng)) {
    return { lat: mem.lat, lng: mem.lng };
  }

  // 2) storage
  const st = getStorage(storageKind);
  if (!st) return null;

  try {
    const raw = st.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw) as Partial<Cached>;
    const lat = Number(obj.lat);
    const lng = Number(obj.lng);
    const ts = Number(obj.ts);

    if (!Number.isFinite(ts)) return null;
    if (Date.now() - ts >= ttlMs) return null;
    if (!isValidLatLng(lat, lng)) return null;

    memoryCache.set(key, { lat, lng, ts });
    return { lat, lng };
  } catch {
    return null;
  }
}

export function writeCachedPosition(pos: LatLng | null, opts: GeoCacheOptions = {}) {
  const key = opts.key ?? DEFAULT_KEY;
  const storageKind = opts.storage ?? 'session';

  const st = getStorage(storageKind);

  if (!pos) {
    memoryCache.delete(key);
    try {
      st?.removeItem(key);
    } catch {}
    return;
  }

  const payload: Cached = { ...pos, ts: Date.now() };
  memoryCache.set(key, payload);

  try {
    st?.setItem(key, JSON.stringify(payload));
  } catch {}
}

/**
 * ✅ 安全版定位：避免 permission prompt 沒回呼導致 Promise 卡死
 * - 不會把錯誤 throw 出去（回傳 ok: false）
 */
export async function getCurrentPositionSafe(
  geoOptions?: PositionOptions,
  watchdogMs = 12000
): Promise<
  { ok: true; pos: LatLng } | { ok: false; reason: 'unavailable' | 'denied' | 'timeout' | 'error' }
> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return { ok: false, reason: 'unavailable' };
  }

  return await new Promise((resolve) => {
    let done = false;
    const finish = (v: any) => {
      if (done) return;
      done = true;
      resolve(v);
    };

    const timer = window.setTimeout(() => finish({ ok: false, reason: 'timeout' }), watchdogMs);

    navigator.geolocation.getCurrentPosition(
      (p) => {
        window.clearTimeout(timer);
        finish({ ok: true, pos: { lat: p.coords.latitude, lng: p.coords.longitude } });
      },
      (err) => {
        window.clearTimeout(timer);
        if (err?.code === 1) finish({ ok: false, reason: 'denied' });
        else finish({ ok: false, reason: 'error' });
      },
      geoOptions
    );
  });
}
